import React, { useState, useEffect, useRef } from "react";
import { Navigation } from "../Navigation";
import { CryptoCard } from "../../Components/CryptoCard";
import { SelectedCoinCard } from "../../Components/SelectedCoinCard";
import { useQuery, useQueryClient } from "react-query";
import { apiUrl } from "../../Components/config";
import { useHttp, useHorizontalScroll } from "../../Hooks";
import {
  ResCrypto,
  ResPlatform,
  SwapQuoteComponentRef,
} from "../../Components/interface";
import { TOKENS } from "../../Components/config";
import {
  Autocomplete,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  CircularProgress,
  Button,
  Tooltip,
  Slider,
  IconButton,
  Menu,
  MenuItem,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { CryptoType, Coin } from "../../Components/interface";
import { LoadingPage } from "../LoadingPage";
import { UniswapQuoteComponent } from "../../Components/Dex/DexUniswap";
import { CurveQuoteComponent } from "../../Components/Dex/DexCurve";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";

export const Cryptos = () => {
  const [animatingId, setAnimatingId] = useState<number>(0);
  const [inputValue, setInputValue] = useState<string>("");
  const [options, setOptions] = useState<Coin[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [SwapLoading, setSwapLoading] = useState<boolean>(false);
  const [selectedCoins, setSelectedCoins] = useState<Coin[]>([]);
  const [loadedCount, setLoadedCount] = useState(0);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [anchorElTo, setAnchorElTo] = useState<null | HTMLElement>(null);
  const [selectedToIndex, setSelectedToIndex] = useState(1);
  const [amountin, setAmountin] = useState(1);

  const open = Boolean(anchorEl);
  const openTo = Boolean(anchorElTo);

  const frames = useRef([]);
  const frameIndex = useRef(0);

  const uniswapComponentRef = useRef<SwapQuoteComponentRef>(null);
  const balanceComponentRef = useRef<SwapQuoteComponentRef>(null);
  const curveComponentRef = useRef<SwapQuoteComponentRef>(null);

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLElement>,
    index: number,
  ) => {
    setSelectedIndex(index);
    setAnchorEl(null);
  };

  const handleToMenuItemClick = (
    event: React.MouseEvent<HTMLElement>,
    index: number,
  ) => {
    setSelectedToIndex(index);
    setAnchorElTo(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuCloseTo = () => {
    setAnchorElTo(null);
  };

  const handleClickTo = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElTo(event.currentTarget);
  };

  const client = useHttp();
  const {
    data: apiData,
    isLoading,
    isError,
    status,
  } = useQuery<ResCrypto, string>(["cryptos"], () => {
    return client("cryptos", { url: apiUrl });
  });

  const handleSelect = async (
    _event: React.SyntheticEvent,
    value: Coin | null,
  ) => {
    if (value) {
      setLoading(true);
      const coinWithPrices = await fetchCoinPrices(value);

      if (coinWithPrices) {
        setSelectedCoins([...selectedCoins, coinWithPrices]);
      }
      setInputValue("");
      //setLoading(false);
    }
  };

  const switchToken = () => {
    const _selectedIndex = selectedIndex;
    const _selectedToIndex = selectedToIndex;
    setSelectedIndex(_selectedToIndex);
    setSelectedToIndex(_selectedIndex);
  };

  useEffect(() => {
    if (selectedCoins.length > 0 && loadedCount === selectedCoins.length) {
      setLoading(false);
    }
  }, [loadedCount, selectedCoins.length]);

  const handleChildLoaded = () => {
    setLoadedCount((prev) => prev + 1); // 子组件加载完成时增加计数
  };

  const fetchCoinPrices = async (coin: Coin) => {
    try {
      const response = await fetch(
        `${apiUrl}/crypto?id=${coin.id}&symbol=${coin.symbol}`,
      );
      if (!response.ok) throw new Error("Failed to fetch prices");
      const platformsData: ResPlatform = await response.json();
      const res = platformsData.payload.sort((a, b) => a.price - b.price);
      return {
        ...coin,
        platforms: res,
      };
    } catch (error) {
      console.error("Error fetching coin prices:", error);
      return null;
    }
  };

  const fetchDexPrices = async (): Promise<void> => {
    setSwapLoading(true);
    try {
      //await balanceComponentRef.current?.fetchQuote();
      await uniswapComponentRef.current?.fetchQuote();
      await curveComponentRef.current?.fetchQuote();
    } catch (error) {
      console.error("loading uniswap failed:", error);
      setSwapLoading(false);
    }
  };

  const handleClose = (id: string) => {
    setSelectedCoins((prevItems) => prevItems.filter((item) => item.id !== id));
  };

  const handleComplete = (): void => {
    setSwapLoading(false);
  };

  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    if (typeof newValue === "number") {
      setAmountin(newValue);
    }
  };

  const handleChangeCommitted = (
    event: Event | React.SyntheticEvent,
    newValue: number | number[],
  ) => {
    if (typeof newValue === "number") {
      setAmountin(newValue);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const data = apiData?.payload;
      const randomId = data
        ? data[Math.floor(Math.random() * data.length)].cid
        : 0;
      setAnimatingId(randomId);

      setTimeout(() => {
        setAnimatingId(0);
      }, 1000);
    }, 5000);

    return () => clearInterval(interval);
  }, [apiData]);

  useEffect(() => {
    const fetchData = async () => {
      if (inputValue.length < 3) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(
          `https://api.coingecko.com/api/v3/search?query=${inputValue}`,
        );
        const data = await response.json();
        setOptions(data.coins || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        setOptions([]);
      }
      setLoading(false);
    };

    const debounceTimer = setTimeout(fetchData, 300);
    return () => clearTimeout(debounceTimer);
  }, [inputValue]);

  return (
    <>
      {(isLoading || loading) && (
        <LoadingPage text="Loading Compare Crypto Price" />
      )}
      <div
        className={`page-content ${!isLoading ? "loaded left-nav-sidebar" : ""}`}
      >
        <Navigation name="crypto" />
        <section className="local-no-padding md-padding-70px-top xs-padding-60px-top sm-padding-60px-top">
          <div className="container">
            <div className="row">
              <div className="top-filter col-12 flex align-items-center justify-content-center px-[15px]">
                <div className={"filter flex gap-[12px]"}></div>
                <form
                  method="post"
                  className="search-collection navbar-form no-padding search-box"
                >
                  <div className="flex">
                    <div className="input-group add-on relative min-w-[300px]">
                      <Autocomplete
                        disablePortal
                        className="cryptosearchAutocomplete"
                        fullWidth
                        value={null}
                        onChange={handleSelect}
                        inputValue={inputValue}
                        onInputChange={(event, newInputValue) =>
                          setInputValue(newInputValue)
                        }
                        options={options}
                        getOptionLabel={(option: Coin) => option.name}
                        loading={loading}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Search Cryptocurrencies..."
                            slotProps={{
                              input: {
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    {loading && <CircularProgress size={20} />}
                                    <InputAdornment position="start">
                                      <img
                                        src="/images/beta.png"
                                        width={"32px"}
                                      />
                                    </InputAdornment>
                                  </>
                                ),
                              },
                            }}
                          />
                        )}
                        renderOption={(props, option: Coin, index) => (
                          <ListItem {...props} key={option.id}>
                            <ListItemAvatar>
                              <Avatar src={option.thumb} alt={option.name}>
                                {option.symbol.charAt(0)}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={option.name}
                              secondary={option.symbol.toUpperCase()}
                            />
                          </ListItem>
                        )}
                      />
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="container-fluid">
            <div className="row">
              <div className="cryptos">
                <div className="cryptos-container">
                  {selectedCoins.map((coin, index) => {
                    return (
                      <SelectedCoinCard
                        key={index}
                        item={coin}
                        onClose={handleClose}
                        onLoad={handleChildLoaded}
                      />
                    );
                  })}
                  <div className={"nft hot-name-berachain hot-badge"}>
                    <div className="main">
                      <img
                        className="tokenImage"
                        src={"/images/crypto/bera.png"}
                        alt={"Berachain"}
                      />
                      <h2>coming soon</h2>
                      <hr />
                      <div className="flex justify-between items-center">
                        <div className="creator">
                          <div className="wrapper">
                            <img
                              src={"/images/exchange/berachain.png"}
                              alt={"Berachain"}
                            />
                          </div>
                          <p>
                            <ins>Berachain</ins>
                          </p>
                        </div>
                        <p>__.__</p>
                      </div>
                    </div>
                  </div>
                  <div
                    className={
                      "nft hot-name-berachain hot-badge selectswaptokens"
                    }
                  >
                    {SwapLoading && (
                      <div className="swap-loading">
                        <span className="loader"></span>
                      </div>
                    )}
                    <div className="main">
                      <h2 className="!text-[#fb8500]">Token Swap Calculator</h2>
                      <div className="swap-contain flex justify-between">
                        <div className="flex flex-col">
                          <p>FROM</p>
                          <div
                            className="swaptoken flex items-center"
                            onClick={handleClick}
                            aria-controls={open ? "account-menu" : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? "true" : undefined}
                          >
                            <div>
                              <IconButton size="small" sx={{ ml: 2 }}>
                                <Avatar
                                  src={
                                    "/images/tokens/" +
                                    Object.entries(TOKENS)[
                                      selectedIndex
                                    ][0].toLowerCase() +
                                    ".png"
                                  }
                                  sx={{ width: 32, height: 32 }}
                                ></Avatar>
                              </IconButton>
                            </div>
                            <div>
                              <span>
                                {Object.entries(TOKENS)[selectedIndex][0]}
                              </span>
                              <ArrowDropDownIcon className="!text-white" />
                            </div>
                          </div>
                          <Menu
                            id="tokenfrom"
                            className="swap-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleMenuClose}
                            MenuListProps={{
                              "aria-labelledby": "lock-button",
                              role: "listbox",
                            }}
                          >
                            {Object.entries(TOKENS).map(
                              ([key, token], index) => {
                                return (
                                  <MenuItem
                                    key={key}
                                    disabled={index === selectedIndex}
                                    selected={index === selectedIndex}
                                    onClick={(event) =>
                                      handleMenuItemClick(event, index)
                                    }
                                  >
                                    <Avatar
                                      className="selectTokenAvatar"
                                      src={
                                        "/images/tokens/" +
                                        key.toLowerCase() +
                                        ".png"
                                      }
                                      alt={token.name}
                                    ></Avatar>
                                    {token.name}
                                  </MenuItem>
                                );
                              },
                            )}
                          </Menu>
                        </div>
                        <div className="swap-switch-button flex items-center">
                          <ChangeCircleIcon onClick={switchToken} />
                        </div>
                        <div className="flex flex-col">
                          <p>TO</p>
                          <div
                            className="swaptoken flex items-center"
                            onClick={handleClickTo}
                            aria-controls={openTo ? "account-menu" : undefined}
                            aria-haspopup="true"
                            aria-expanded={openTo ? "true" : undefined}
                          >
                            <div>
                              <IconButton size="small" sx={{ ml: 2 }}>
                                <Avatar
                                  src={
                                    "/images/tokens/" +
                                    Object.entries(TOKENS)[
                                      selectedToIndex
                                    ][0].toLowerCase() +
                                    ".png"
                                  }
                                  sx={{ width: 32, height: 32 }}
                                ></Avatar>
                              </IconButton>
                            </div>
                            <div>
                              <span>
                                {Object.entries(TOKENS)[selectedToIndex][0]}
                              </span>
                              <ArrowDropDownIcon className="!text-white" />
                            </div>
                          </div>
                          <Menu
                            id="tokento"
                            className="swap-menu"
                            anchorEl={anchorElTo}
                            open={openTo}
                            onClose={handleMenuCloseTo}
                            MenuListProps={{
                              "aria-labelledby": "lock-button",
                              role: "listbox",
                            }}
                          >
                            {Object.entries(TOKENS).map(
                              ([key, token], index) => {
                                return (
                                  <MenuItem
                                    key={key}
                                    disabled={index === selectedToIndex}
                                    selected={index === selectedToIndex}
                                    onClick={(event) =>
                                      handleToMenuItemClick(event, index)
                                    }
                                  >
                                    <Avatar
                                      className="selectTokenAvatar"
                                      src={
                                        "/images/tokens/" +
                                        key.toLowerCase() +
                                        ".png"
                                      }
                                      alt={token.name}
                                    ></Avatar>
                                    {token.name}
                                  </MenuItem>
                                );
                              },
                            )}
                          </Menu>
                        </div>
                      </div>
                      <div className="swap-slider my-[1rem] mx-[15px]">
                        <Slider
                          value={amountin}
                          onChange={handleSliderChange}
                          onChangeCommitted={handleChangeCommitted}
                          min={1}
                          max={100}
                          aria-label="30px"
                          valueLabelDisplay="on"
                        />
                      </div>
                      <div className="mb-[1rem]">
                        <Button
                          className="!bg-[#fb8500]"
                          onClick={fetchDexPrices}
                          variant="contained"
                          endIcon={<TrackChangesIcon className="text-white" />}
                          fullWidth
                        >
                          Convert Currency
                        </Button>
                      </div>
                      <hr />
                      <UniswapQuoteComponent
                        ref={uniswapComponentRef}
                        OnComplete={handleComplete}
                        tokenIn={Object.entries(TOKENS)[selectedIndex][0]}
                        tokenOut={Object.entries(TOKENS)[selectedToIndex][0]}
                        amountIn={amountin}
                        fee={3000}
                      />
                      <CurveQuoteComponent
                        ref={curveComponentRef}
                        OnComplete={handleComplete}
                        tokenIn={Object.entries(TOKENS)[selectedIndex][0]}
                        tokenOut={Object.entries(TOKENS)[selectedToIndex][0]}
                        amountIn={amountin}
                        fee={3000}
                      />
                    </div>
                  </div>
                  {apiData?.payload.map((crypto, index) => {
                    return (
                      <CryptoCard
                        key={index}
                        item={crypto}
                        value={crypto.platforms[0].price}
                        isAnimating={crypto.cid === animatingId}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
